/**
 * The organisation's currency, for every amount the console shows. The platform keeps the ledger in
 * US dollars and tells every person the currency their organisation chose and the rate to show it at
 * (`money` on the organisation profile); `useMoney` formats with it. Until the profile has loaded, or
 * when the organisation has no rate, amounts are US dollars.
 */

import { CURRENCY_NAMES, currencySymbol, formatMoney, shownCurrency, toDisplay, toUsd, USD_MONEY, type OrganisationMoney } from '@aioe/shared';
import { createContext, useContext, useEffect, type ReactNode } from 'react';

import { routes } from './api/client.ts';
import { useLoad } from './hooks.ts';
import { setModelMoney } from './workbench/moneyFormat.ts';

const MoneyContext = createContext<{ money: OrganisationMoney; reload: () => void }>({ money: USD_MONEY, reload: () => {} });

/** Loads the organisation's currency once a person is signed in; a failure leaves US dollars. */
export function MoneyProvider({ children }: { children: ReactNode }) {
  const org = useLoad(() => routes.organisationProfile().catch(() => undefined), [], 6 * 3_600_000);
  const money = org.data?.money;
  // The views ported from the workbench format costs in their models (workbench/moneyFormat.ts).
  useEffect(() => setModelMoney(money ?? USD_MONEY), [money]);
  return <MoneyContext.Provider value={{ money: org.data?.money ?? USD_MONEY, reload: org.reload }}>{children}</MoneyContext.Provider>;
}

export function useMoney() {
  const { money, reload } = useContext(MoneyContext);
  const code = shownCurrency(money);
  return {
    money,
    reload,
    /** The code amounts are shown in: the organisation's, or USD while it has no rate. */
    code,
    symbol: currencySymbol(code),
    /** "Australian dollars" */
    name: `${CURRENCY_NAMES[code]}s`.replace(/ys$/, 'ies').replace(/^Euros$/, 'euros'),
    /** A US-dollar amount, formatted in the organisation's currency. */
    format: (usd: number, opts?: { digits?: number; compact?: boolean }) => formatMoney(usd, money, opts),
    toDisplay: (usd: number) => toDisplay(usd, money),
    toUsd: (amount: number) => toUsd(amount, money),
  };
}
